import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Avatar, AvatarGradientFallback, Button, Card, cn, initialsFor, Text } from "@athena/ui";
import { LOST_CONTACT_SUGGESTED_AFTER_INTERVALS } from "@athena/common/utils/clientEngagement";
import { ALUMNI_ROOT } from "@/lib/adminPilot";
import { fullName, parseCalendarDate } from "../helpers/client";
import { useOpenedFromHere } from "../helpers/openedFrom";
import { formatMilestoneDay, milestoneWhen } from "../helpers/milestones";
import type { QueueSection, QueueEntry } from "../helpers/outreachQueue";
import type { LoggedContact } from "./LogContactForm";
import { PILL_BUTTON, LogContactForm } from "./LogContactForm";
import { MarkLostContact } from "./MarkLostContact";
import { MilestoneBadge } from "./MilestoneBadge";

/** The due date's color says how urgent it is, matching the section heading's count. */
const DUE_TONE = {
    overdue: "text-destructive font-semibold",
    today: "text-warning",
    thisWeek: "text-text-muted",
    later: "text-text-muted",
} as const satisfies Record<QueueSection, string>;

interface OutreachQueueItemProps {
    entry: QueueEntry;
    today: Date;
    /** Whether the inline log is showing. */
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    /** Saves the contact. Once it has, the queue is re-read and this client usually leaves it. */
    onLog: (contact: LoggedContact) => Promise<void>;
    onOpenFullLog: () => void;
}

/**
 * One person to reach: who, why, how, and how late. A birthday or sober anniversary wears its badge,
 * which says whether it is today or a week out. "Log result" opens the whole log in the row
 * itself, one question at a time; "Open full log" has the same in a dialog. After six check-ins
 * without reaching them, the row also offers "Mark lost contact".
 */
export function OutreachQueueItem({
    entry,
    today,
    expanded,
    onExpandedChange,
    onLog,
    onOpenFullLog,
}: OutreachQueueItemProps) {
    const { t } = useTranslation();
    const openedFrom = useOpenedFromHere();
    const ids = useId();
    const { client, section, daysOverdue, channel, milestone, prepNote } = entry;

    const dueLabel =
        section === "overdue"
            ? t("admin.alumni.queue.overdue", { count: daysOverdue })
            : section === "today"
              ? t("admin.alumni.queue.dueToday")
              : t("admin.alumni.queue.dueIn", { count: -daysOverdue });
    const milestoneDay = formatMilestoneDay(parseCalendarDate(entry.dueDate));
    const reasonLabel =
        milestone?.kind === "birthday"
            ? t("admin.alumni.queue.milestone.birthday", { date: milestoneDay })
            : milestone?.kind === "soberAnniversary"
              ? t("admin.alumni.queue.milestone.soberAnniversary", {
                    years: t("admin.alumni.milestones.years", { count: milestone.years }),
                    date: milestoneDay,
                })
              : entry.reason && t(`admin.alumni.queue.reason.${entry.reason}`);
    const summary = [
        reasonLabel,
        channel ? t(`admin.alumni.channel.${channel}`) : undefined,
    ].filter(Boolean);

    return (
        <Card width="full" className="overflow-visible p-4 md:p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                <div className="flex min-w-0 flex-1 items-start gap-4">
                    <Avatar size="lg" className="shrink-0">
                        <AvatarGradientFallback>
                            {initialsFor(client.firstName, client.lastName)}
                        </AvatarGradientFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <Link
                                to={`${ALUMNI_ROOT}/${client.id}`}
                                state={openedFrom}
                                className="font-display text-text-heading text-base font-semibold hover:underline"
                            >
                                {fullName(client)}
                            </Link>
                            {milestone?.kind === "birthday" ? (
                                <MilestoneBadge kind="birthday" when={milestoneWhen(-daysOverdue)} />
                            ) : milestone?.kind === "soberAnniversary" ? (
                                <MilestoneBadge
                                    kind="soberAnniversary"
                                    years={milestone.years}
                                    when={milestoneWhen(-daysOverdue)}
                                />
                            ) : null}
                        </div>
                        <Text size="sm" muted className="mt-1.5">
                            {summary.join(" · ")}
                        </Text>
                        {prepNote ? (
                            <Text size="sm" className="text-text-body mt-1">
                                {t("admin.alumni.queue.prepNote", { note: prepNote })}
                            </Text>
                        ) : null}
                    </div>
                </div>
                <div className="flex shrink-0 items-center justify-between gap-3 pl-16 sm:flex-col sm:items-end sm:pl-0">
                    <span className={cn("font-mono text-sm whitespace-nowrap", DUE_TONE[section])}>{dueLabel}</span>
                    <Button
                        variant="outline"
                        className={PILL_BUTTON}
                        aria-expanded={expanded}
                        aria-controls={`${ids}-log`}
                        onClick={() => onExpandedChange(!expanded)}
                    >
                        {expanded ? t("common.actions.cancel") : t("admin.alumni.queue.logResult")}
                    </Button>
                </div>
            </div>
            {client.lostContactSuggested ? (
                <div className="border-border mt-4 flex flex-col gap-3 border-t border-dashed pt-4 sm:flex-row sm:items-center sm:justify-between md:ml-16">
                    <Text size="sm" muted>
                        {t("admin.alumni.lostContact.suggestion", {
                            count: LOST_CONTACT_SUGGESTED_AFTER_INTERVALS,
                            firstName: client.firstName,
                        })}
                    </Text>
                    <MarkLostContact client={client} className={PILL_BUTTON} />
                </div>
            ) : null}
            {expanded ? (
                <div id={`${ids}-log`} className="border-border mt-4 border-t border-dashed pt-4 md:ml-16">
                    {/* Unmounted on close, so each opening starts blank. */}
                    <LogContactForm
                        layout="inline"
                        clientId={client.id}
                        today={today}
                        plannedChannel={channel}
                        onSubmit={onLog}
                        onOpenFullLog={onOpenFullLog}
                    />
                </div>
            ) : null}
        </Card>
    );
}
